import { selectStandardProps } from "@/utils/componentUtils";
import { resolveFormValidationError, setErrorsToForm } from "@/utils/formUtils";
import { validations } from "@/utils/validationUtils";
import { Checkbox, Col, Form, Input, Modal, Row, Select } from "antd";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useCreateConfigProperty, useUpdateConfigProperty } from "../../../../common/api/queries/dynamicConfig";
import ActionButton from "../../../../common/components/buttons/ActionButton";
import HiddenInput from "../../../../common/components/form/components/HiddenInput";
import DeleteIcon from "../../../../common/components/icons/DeleteIcon";
import { ModalSizes, rowGutter } from "../../../../common/constants";
import { Feature } from "../../../../common/security/enums";
import { TopAgentSelect } from "../../../enumerations/components/form/TopAgentSelect";
import { CreateUpdateDynamicConfigProperty, DynamicConfigProperty } from "../../types";
import { CONFIG_PROPERTY_EMAIL_KEYS, CONFIG_PROPERTY_FEATURE_KEYS, CONFIG_PROPERTY_PHONE_KEYS } from "../../utils";

const FormItemValue = () => {
  const { t } = useTranslation();

  const form = Form.useFormInstance<CreateUpdateDynamicConfigProperty & { valueArray?: string[] }>();
  const encrypted = Form.useWatch("encrypted", form);
  const key = Form.useWatch("key", form);

  if (CONFIG_PROPERTY_EMAIL_KEYS.includes(key)) {
    return (
      <>
        <Form.List name="valueArray" initialValue={[""]}>
          {(fields, { add, remove }) => (
            <>
              <Row gutter={rowGutter}>
                {fields.map(({ key, ...field }) => (
                  <React.Fragment key={key}>
                    <Col span={10}>
                      <Form.Item
                        {...field}
                        name={field.name}
                        label={t("configProperty.attrs.value")}
                        rules={[validations.notBlank, validations.size(1, 254), validations.email]}
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col span={2}>{fields.length > 1 && <DeleteIcon onClick={() => remove(field.name)} />}</Col>
                  </React.Fragment>
                ))}
              </Row>
              <ActionButton icon="plus" onClick={() => add()}>
                {t("common.add")}
              </ActionButton>
            </>
          )}
        </Form.List>

        <HiddenInput name="value" />
        <HiddenInput name="encrypted" initialValue={false} />
      </>
    );
  }

  if (CONFIG_PROPERTY_PHONE_KEYS.includes(key)) {
    return (
      <>
        <Form.List name="valueArray" initialValue={[""]}>
          {(fields, { add, remove }) => (
            <>
              <Row gutter={rowGutter}>
                {fields.map(({ key, ...field }) => (
                  <React.Fragment key={key}>
                    <Col span={10}>
                      <Form.Item
                        {...field}
                        name={field.name}
                        label={t("configProperty.attrs.value")}
                        rules={[validations.notBlank, validations.mobilePhoneNumber()]}
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col span={2}>{fields.length > 1 && <DeleteIcon onClick={() => remove(field.name)} />}</Col>
                  </React.Fragment>
                ))}
              </Row>
              <ActionButton icon="plus" onClick={() => add()}>
                {t("common.add")}
              </ActionButton>
            </>
          )}
        </Form.List>

        <HiddenInput name="value" />
        <HiddenInput name="encrypted" initialValue={false} />
      </>
    );
  }

  if (CONFIG_PROPERTY_FEATURE_KEYS.includes(key)) {
    return (
      <>
        <Form.Item name="valueArray" label={t("configProperty.attrs.value")} rules={[validations.notNull]}>
          <Select
            {...selectStandardProps}
            mode="multiple"
            maxTagCount="responsive"
            options={Object.keys(Feature).map((feature) => ({
              value: feature,
              label: t("agent.enums.feature." + feature),
            }))}
          />
        </Form.Item>

        <HiddenInput name="value" />
        <HiddenInput name="encrypted" initialValue={false} />
      </>
    );
  }

  return (
    <Row gutter={rowGutter}>
      <Col span={16}>
        {encrypted ? (
          <Form.Item name="value" label={t("configProperty.attrs.value")} rules={[validations.size(1, 8192)]}>
            <Input.Password />
          </Form.Item>
        ) : (
          <Form.Item name="value" label={t("configProperty.attrs.value")} rules={[validations.size(1, 8192)]}>
            <Input.TextArea rows={3} />
          </Form.Item>
        )}
      </Col>

      <Col span={8}>
        <Form.Item
          name="encrypted"
          className="form-item-without-label"
          valuePropName="checked"
          initialValue={false}
          rules={[validations.none]}
        >
          <Checkbox>{t("configProperty.attrs.encrypted")}</Checkbox>
        </Form.Item>
      </Col>
    </Row>
  );
};

interface Props {
  open: boolean;
  property?: DynamicConfigProperty;
  onFormCancel: () => void;
}

export const ConfigPropertyModalForm = ({ open, property, onFormCancel }: Props) => {
  const { t } = useTranslation();

  const [form] = Form.useForm<CreateUpdateDynamicConfigProperty & { valueArray?: string[] }>();

  const createConfigProperty = useCreateConfigProperty();
  const updateConfigProperty = useUpdateConfigProperty();

  useEffect(() => {
    if (open && property) {
      form.setFieldsValue({
        ...property,
        valueArray: property.value?.split(",").map((value) => value?.trim()),
        ownedById: property.ownedBy?.id,
      });
    }
  }, [property, open, form]);

  const handleFormSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        if (property) {
          updateConfigProperty.mutate(
            { id: property.id, object: values },
            {
              onSuccess: onFormCancel,
              onError: (error) => setErrorsToForm(form, "configProperty.attrs", error.violations),
            },
          );
        } else {
          createConfigProperty.mutate(values, {
            onSuccess: onFormCancel,
            onError: (error) => setErrorsToForm(form, "configProperty.attrs", error.violations),
          });
        }
      })
      .catch(resolveFormValidationError);
  };

  return (
    <Modal
      width={ModalSizes.MEDIUM}
      open={open}
      title={property ? t("configProperty.titles.updateProperty") : t("configProperty.titles.createProperty")}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      confirmLoading={createConfigProperty.isPending || updateConfigProperty.isPending}
      afterClose={() => form.resetFields()}
      onOk={handleFormSubmit}
      onCancel={onFormCancel}
      mask={{ closable: false }}
    >
      <Form
        form={form}
        layout="vertical"
        name="configPropertyForm"
        onValuesChange={(_, values) => {
          if (
            CONFIG_PROPERTY_EMAIL_KEYS.includes(values.key) ||
            CONFIG_PROPERTY_PHONE_KEYS.includes(values.key) ||
            CONFIG_PROPERTY_FEATURE_KEYS.includes(values.key)
          ) {
            form.setFieldsValue({ value: values.valueArray?.join(",") });
          }
        }}
      >
        <HiddenInput name="optimisticLockVersion" />
        <Row gutter={rowGutter}>
          <Col span={16}>
            <Form.Item
              name="key"
              label={t("configProperty.attrs.key")}
              rules={[validations.notBlank, validations.size(1, 255)]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={8}>
            <TopAgentSelect
              formItemProps={{
                name: "ownedById",
                label: t("configProperty.attrs.ownedById"),
                rules: [validations.none],
              }}
              selectProps={{ allowClear: true }}
            />
          </Col>
        </Row>

        <FormItemValue />
      </Form>
    </Modal>
  );
};
